import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  InputAdornment,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import RadiologyIcon from '@mui/icons-material/MonitorHeart'
import SearchIcon from '@mui/icons-material/Search'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useNavigate } from 'react-router-dom'
import API from '../../../services/API'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import EmptyState from '../../../components/EmptyState/EmptyState'
import Age from '../../../components/Age'
import {
  StatTile,
  TabLabel,
} from '../../fleetEquipment/vehicles/VehicleComponents/SectionKit'
import ImagingReviewDrawer, {
  IMAGING_STATUS_COLORS,
  ModalityChip,
  bodyLocation,
  fullName,
  patientLabel,
} from '../ePCR/Imaging/ImagingReviewDrawer'

// Approver worklist - every imaging request across the organisation. Only
// users configured as imaging approvers in Site Configuration can load it.
// "To review" is a card queue (oldest first); History is a searchable grid.
// Both open ImagingReviewDrawer.

const HISTORY_FILTERS = ['All', 'Approved', 'Completed', 'Rejected']

// Short chip text for the grid; the drawer uses the longer IMAGING_STATUS_LABELS
const GRID_STATUS_LABELS = { Approved: 'Awaiting study' }

const patientLine = (epcr) => {
  const age = epcr?.demographics?.dob ? Age(epcr.demographics.dob, epcr.createdAt) : null
  return [patientLabel(epcr), age, epcr?.epcrNumber && `ePCR #${epcr.epcrNumber}`]
    .filter(Boolean)
    .join(' · ')
}

const PendingCard = ({ request, onOpen }) => {
  const pregnancyMissing = request.modality?.ionising && !request.pregnancyStatus
  const waitingHours = moment().diff(request.createdAt, 'hours')
  return (
    <Card
      variant="outlined"
      onClick={onOpen}
      sx={{
        p: 1.75,
        borderRadius: 2.5,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 0,
        borderLeft: (t) => `4px solid ${t.palette.warning.main}`,
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': { borderColor: 'warning.main', boxShadow: 2 },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <ModalityChip modality={request.modality} />
        <Tooltip title={moment(request.createdAt).format('Do MMM YYYY [at] HH:mm')}>
          <Typography
            variant="caption"
            color={waitingHours >= 2 ? 'error.main' : 'text.secondary'}
            fontWeight={waitingHours >= 2 ? 700 : 400}
            noWrap
          >
            {moment(request.createdAt).fromNow()}
          </Typography>
        </Tooltip>
      </Stack>

      <Box minWidth={0}>
        <Typography variant="body1" fontWeight={700} noWrap>
          {bodyLocation(request) || 'Body location not given'}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {patientLine(request.epcr)}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          whiteSpace: 'pre-line',
        }}
      >
        {request.clinicalIndication}
      </Typography>

      {pregnancyMissing && (
        <Chip
          size="small"
          color="error"
          variant="outlined"
          icon={<ErrorOutlineIcon />}
          label="Pregnancy status missing"
          sx={{ alignSelf: 'flex-start', borderRadius: 1, fontWeight: 700 }}
        />
      )}

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" mt="auto" pt={0.5}>
        <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
          <Avatar src={request.requestedBy?.photograph} sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>
            {request.requestedBy?.firstName?.[0]}
          </Avatar>
          <Typography variant="caption" color="text.secondary" noWrap>
            {fullName(request.requestedBy)}
          </Typography>
        </Stack>
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={(e) => {
            e.stopPropagation()
            onOpen()
          }}
          sx={{ borderRadius: 2, textTransform: 'none', flexShrink: 0 }}
        >
          Review
        </Button>
      </Stack>
    </Card>
  )
}

const PersonCell = ({ user, caption }) =>
  user ? (
    <Stack direction="row" spacing={1} alignItems="center" height="100%" minWidth={0}>
      <Avatar src={user.photograph} sx={{ width: 26, height: 26, fontSize: '0.75rem' }}>
        {user.firstName?.[0]}
      </Avatar>
      <Box minWidth={0}>
        <Typography variant="body2" noWrap>
          {fullName(user)}
        </Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {caption}
          </Typography>
        )}
      </Box>
    </Stack>
  ) : (
    <Typography variant="caption" color="text.secondary">
      —
    </Typography>
  )

const ImagingRequests = () => {
  const [loading, setLoading] = useState(true)
  const [notApprover, setNotApprover] = useState(false)
  const [fetchError, setFetchError] = useState(false)
  const [requests, setRequests] = useState([])
  const [tab, setTab] = useState('pending')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('All')
  const [selected, setSelected] = useState(null)
  const navigate = useNavigate()

  const loadRequests = () =>
    API.get('/imaging-requests')
      .then((res) => {
        if (res.status === 200) {
          setRequests(res.data.requests ?? [])
          setNotApprover(false)
          setFetchError(false)
        } else if (res.status === 403) {
          setNotApprover(true)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))

  useEffect(() => {
    loadRequests()
  }, [])

  // Oldest first — the request that has waited longest is the one to do next
  const pending = useMemo(
    () =>
      requests
        .filter((r) => r.status === 'Requested')
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
    [requests],
  )
  const history = useMemo(
    () => requests.filter((r) => !['Requested', 'Cancelled'].includes(r.status)),
    [requests],
  )

  const thisMonth = (date) => date && moment(date).isSame(moment(), 'month')
  const awaitingStudy = requests.filter((r) => r.status === 'Approved').length
  const completedThisMonth = requests.filter(
    (r) => r.status === 'Completed' && thisMonth(r.clinicalImpression?.recordedAt ?? r.updatedAt),
  ).length
  const rejectedThisMonth = requests.filter((r) => r.status === 'Rejected' && thisMonth(r.decidedAt)).length
  const oldest = pending[0]

  const filteredHistory = useMemo(() => {
    const term = search.trim().toLowerCase()
    return history.filter((r) => {
      if (statusFilter !== 'All' && r.status !== statusFilter) return false
      if (!term) return true
      return [
        patientLabel(r.epcr),
        r.epcr?.epcrNumber,
        r.modality?.label,
        bodyLocation(r),
        r.clinicalIndication,
        fullName(r.requestedBy),
        fullName(r.decidedBy),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term)
    })
  }, [history, search, statusFilter])

  const columns = [
    {
      field: 'createdAt',
      headerName: 'Requested',
      minWidth: 120,
      valueGetter: (value) => (value ? new Date(value) : null),
      renderCell: ({ row }) => (
        <Box>
          <Typography variant="body2">{moment(row.createdAt).format('DD/MM/YYYY')}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {moment(row.createdAt).format('HH:mm')}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'patient',
      headerName: 'Patient',
      flex: 1,
      minWidth: 200,
      valueGetter: (value, row) => patientLabel(row.epcr),
      renderCell: ({ row }) => (
        <Box minWidth={0}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {patientLabel(row.epcr)}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {[
              row.epcr?.demographics?.dob && Age(row.epcr.demographics.dob, row.epcr.createdAt),
              row.epcr?.epcrNumber && `ePCR #${row.epcr.epcrNumber}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'study',
      headerName: 'Study',
      flex: 1,
      minWidth: 220,
      valueGetter: (value, row) => `${row.modality?.label ?? ''} ${bodyLocation(row)}`,
      renderCell: ({ row }) => (
        <Stack spacing={0.5} minWidth={0} py={1}>
          <ModalityChip modality={row.modality} />
          <Typography variant="caption" color="text.secondary" noWrap>
            {bodyLocation(row) || '—'}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'requestedBy',
      headerName: 'Requested by',
      minWidth: 170,
      sortable: false,
      renderCell: ({ row }) => <PersonCell user={row.requestedBy} />,
    },
    {
      field: 'decision',
      headerName: 'Decision',
      minWidth: 190,
      sortable: false,
      renderCell: ({ row }) => (
        <PersonCell
          user={row.decidedBy}
          caption={row.decidedAt ? moment(row.decidedAt).format('DD MMM YYYY HH:mm') : undefined}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 140,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={GRID_STATUS_LABELS[row.status] ?? row.status}
          color={IMAGING_STATUS_COLORS[row.status] ?? 'default'}
          variant={row.status === 'Completed' ? 'filled' : 'outlined'}
          sx={{ borderRadius: 1, fontWeight: 700 }}
        />
      ),
    },
  ]

  return (
    <Stack gap={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap gap={1}>
        <Box>
          <Typography variant="title2" component="div">
            Imaging Requests
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            Approve imaging studies requested by crews from the ePCR
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<SettingsOutlinedIcon />}
          onClick={() => navigate('/site-configuration')}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          Imaging settings
        </Button>
      </Stack>

      {loading && <LinearProgress />}

      {notApprover && (
        <Alert severity="warning">
          Only configured imaging approvers can view the imaging worklist. Approvers are managed
          in Site Configuration → Patient Records.
        </Alert>
      )}
      {fetchError && <Alert severity="error">Unable to load imaging requests. Please try again later.</Alert>}

      {!loading && !notApprover && !fetchError && (
        <>
          <Box
            display="grid"
            gap={1}
            gridTemplateColumns={{ xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }}
          >
            <StatTile
              label="Awaiting approval"
              value={String(pending.length)}
              tone={pending.length ? 'warning' : 'success'}
              caption={oldest ? `Oldest ${moment(oldest.createdAt).fromNow()}` : 'All caught up'}
            />
            <StatTile
              label="Awaiting study"
              value={String(awaitingStudy)}
              tone={awaitingStudy ? 'info' : undefined}
              caption="Approved, no impression yet"
            />
            <StatTile
              label="Completed this month"
              value={String(completedThisMonth)}
              tone="success"
              caption={moment().format('MMMM YYYY')}
            />
            <StatTile
              label="Rejected this month"
              value={String(rejectedThisMonth)}
              tone={rejectedThisMonth ? 'error' : undefined}
              caption={moment().format('MMMM YYYY')}
            />
          </Box>

          {requests.length === 0 ? (
            <EmptyState
              icon={RadiologyIcon}
              title="No imaging requests yet"
              description="When clinicians request an imaging study from the ePCR Assess tab, the requests appear here for approval."
              steps={[
                'Open Site Configuration → Patient Records',
                'Enable imaging requests and choose your modalities and approvers',
                'Requests raised in the ePCR will be listed here',
              ]}
              actions={[{ label: 'Configure Imaging', to: '/site-configuration' }]}
            />
          ) : (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
                  <Tab
                    value="pending"
                    label={
                      <TabLabel
                        label="To review"
                        count={pending.length}
                        color={pending.length ? 'warning' : 'default'}
                      />
                    }
                    sx={{ textTransform: 'none' }}
                  />
                  <Tab
                    value="history"
                    label={<TabLabel label="History" count={history.length} />}
                    sx={{ textTransform: 'none' }}
                  />
                </Tabs>
              </Box>

              {tab === 'pending' &&
                (pending.length === 0 ? (
                  <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
                    You're all caught up — no imaging requests waiting for approval.
                  </Alert>
                ) : (
                  <Box
                    display="grid"
                    gap={1.5}
                    gridTemplateColumns={{
                      xs: '1fr',
                      md: 'repeat(2, minmax(0, 1fr))',
                      xl: 'repeat(3, minmax(0, 1fr))',
                    }}
                  >
                    {pending.map((request) => (
                      <PendingCard key={request._id} request={request} onOpen={() => setSelected(request)} />
                    ))}
                  </Box>
                ))}

              {tab === 'history' && (
                <>
                  <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ sm: 'center' }}>
                    <TextField
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      size="small"
                      placeholder="Search patient, ePCR, study, clinician…"
                      sx={{ flex: 1, maxWidth: { sm: 420 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={statusFilter}
                      onChange={(e, v) => v && setStatusFilter(v)}
                    >
                      {HISTORY_FILTERS.map((f) => (
                        <ToggleButton key={f} value={f} sx={{ textTransform: 'none', px: 1.5 }}>
                          {GRID_STATUS_LABELS[f] ?? f}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Stack>
                  <CustomDataGrid
                    norowsoverlay={
                      <Alert severity="info">
                        {history.length === 0 ? 'No decided requests yet.' : 'No requests match your search.'}
                      </Alert>
                    }
                    rows={filteredHistory}
                    columns={columns}
                    getRowId={(row) => row._id}
                    getRowHeight={() => 'auto'}
                    pagination
                    pageSizeOptions={[10, 25, 50, 100]}
                    initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                    disableRowSelectionOnClick
                    onRowClick={({ row }) => setSelected(row)}
                    sx={{
                      '.MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                      '.MuiDataGrid-cell:focus': { outline: 'none' },
                      '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
                    }}
                    slots={{ toolbar: () => null }}
                  />
                </>
              )}
            </>
          )}
        </>
      )}

      <ImagingReviewDrawer
        request={selected}
        onClose={() => setSelected(null)}
        onDecided={(updated) => {
          // Keep the populated ePCR summary the decision response doesn't carry
          setRequests((prev) => prev.map((r) => (r._id === updated._id ? { ...updated, epcr: r.epcr } : r)))
          setSelected(null)
        }}
      />
    </Stack>
  )
}

export default ImagingRequests
